import React from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material";
import { Add, Edit, Delete, SubdirectoryArrowRight } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Subcategory, subcategoriesService, SubcategoryTreeNode } from "../../../../services/subcategories";

interface SubcategoriesTabProps {
  subcategories: Subcategory[];
  onEdit: (subcategory?: Subcategory) => void;
  onDelete: (id: string) => void;
}

const SubcategoriesTab: React.FC<SubcategoriesTabProps> = ({
  subcategories,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();

  // Build hierarchy tree for display with indentation
  const tree = subcategoriesService.buildTree(subcategories);

  const renderNode = (node: SubcategoryTreeNode, depth: number = 0): React.ReactNode => (
    <Card key={node.id} sx={{ height: "100%", ml: depth > 0 ? depth * 3 : 0 }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box sx={{ flex: 1, pr: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              <Typography
                component="span"
                variant="caption"
                sx={{
                  bgcolor: depth > 0 ? "rgba(0,212,255,0.1)" : "transparent",
                  px: 0.5,
                  borderRadius: 0.5,
                }}
              >
                {t("admin.contentManagement.fields.key")}
              </Typography>{" "}
              ({node.category_key})
              {node.parent_id && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  <SubdirectoryArrowRight sx={{ fontSize: 12, verticalAlign: "middle" }} />
                  {t("admin.contentManagement.depth", { defaultValue: "Level" })} {depth + 1}
                </Typography>
              )}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "1rem", sm: "1.1rem" },
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              {depth > 0 && (
                <Typography
                  component="span"
                  variant="caption"
                  color="text.disabled"
                  sx={{ fontSize: "0.7rem" }}
                >
                  {'\u2502'.repeat(depth)}{'\u2514'}
                </Typography>
              )}
              {node.key}
            </Typography>
          </Box>
          <Chip
            label={
              node.is_active
                ? t("admin.contentManagement.active")
                : t("admin.contentManagement.inactive")
            }
            color={node.is_active ? "success" : "default"}
            size="small"
          />
        </Box>

        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t("admin.contentManagement.fields.labelEn")}
          </Typography>
          <Typography variant="body2">
            {node.label_en || "-"}
          </Typography>
        </Box>

        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t("admin.contentManagement.fields.labelAr")}
          </Typography>
          <Typography variant="body2">
            {node.label_ar || "-"}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 1,
          }}
        >
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            <Chip
              label={t("admin.contentManagement.fields.displayOrder") + ": " + node.display_order}
              size="small"
              sx={{ bgcolor: "rgba(0,0,0,0.04)" }}
            />
            {node.slug && (
              <Chip
                label={"/" + node.slug}
                size="small"
                variant="outlined"
                sx={{ fontSize: "0.7rem" }}
              />
            )}
            {node.children.length > 0 && (
              <Chip
                label={t("admin.contentManagement.children", { defaultValue: "{count} children", count: node.children.length })}
                size="small"
                color="info"
                variant="outlined"
              />
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title={t("admin.contentManagement.edit")}>
              <IconButton
                size="small"
                onClick={() => onEdit(node)}
                sx={{ color: "#00d4ff" }}
              >
                <Edit />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("admin.contentManagement.delete")}>
              <IconButton
                size="small"
                onClick={() => onDelete(node.id)}
                sx={{ color: "#ff3366" }}
              >
                <Delete />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          mb: 3,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
        >
          {t("admin.contentManagement.subcategories", { defaultValue: "Subcategories" })}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => onEdit()}
          sx={{
            background: "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
            boxShadow: "0 2px 8px rgba(0,123,255,0.2)",
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {t("admin.contentManagement.addSubcategory", { defaultValue: "Add Subcategory" })}
        </Button>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)",
          },
          gap: { xs: 2, sm: 2.5, md: 3 },
        }}
      >
        {/* Render full hierarchy recursively */}
        {tree.flatMap((node) => {
          const renderBranch = (n: SubcategoryTreeNode, depth: number): React.ReactNode[] => [
            renderNode(n, depth),
            ...n.children.flatMap((child) => renderBranch(child, depth + 1)),
          ];
          return renderBranch(node, 0);
        })}
      </Box>

      {subcategories.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
          {t("common.noItems", { defaultValue: "No subcategories yet. Click 'Add Subcategory' to create one." })}
        </Typography>
      )}
    </Box>
  );
};

export default SubcategoriesTab;